import React from 'react';
import styled from '@emotion/styled';
import _ from 'lodash';

import Button from '~ComponentsImproved/Button';
import { useCurrency, useI18n } from '~Hooks';

const Wrapper = styled.div`
  background-color: #000;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  height: 100%;
  padding: 20px;
  z-index: 4;
`;

const InnerWrapper = styled.div`
  max-width: 400px;
  margin: 0 auto;
`;

const Title = styled.div`
  font-size: 24px;
  color: var(--colorBlueGray500);
  text-align: center;
  margin-bottom: 24px;
`;

const Amount = styled.div`
  font-size: 32px;
  color: var(--colorBlueGray200);
  font-weight: bold;
  text-align: center;
  margin-bottom: 4px;
`;

const TipOptions = styled.div`
  display: grid;
  font-size: 32px;
  grid-template-columns: repeat(auto-fill, minmax(154px, 1fr));
  margin: 16px -4px;
`;

const TipOption = styled.div<{
  highlighted: boolean;
}>`
  background: var(--colorBlueGray1200);
  box-sizing: border-box;
  text-align: center;
  color: var(--colorBlueGray200);
  margin: 4px;
  border-radius: 4px;
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.64);
  padding: 18px 8px 22px;
  font-size: 32px;
  font-weight: normal;
  height: 180px;
  letter-spacing: 0px;
  line-height: 40px;
  border: 5px solid transparent;

  ${({ highlighted }) =>
    highlighted &&
    `
   border: 5px solid var(--colorBlueGray500);
  }`}
`;

const TipAmount = styled.div`
  font-size: 32px;
  font-weight: bold;
  letter-spacing: 0px;
  line-height: 40px;
  color: var(--colorBlueGray200);
  padding: 20px;
  margin: 10px;
  border-radius: 4px;
`;

const TipTotal = styled.div`
  font-size: 19px;
  margin-top: 10px;
  font-weight: normal;
  letter-spacing: 0px;
  line-height: 40px;
  color: var(--colorBlueGray600);
`;

type Props = {
  onSubmit: (percentage: number) => void;
  onClose?: () => void;
  title: string;
  submitText?: string;
  paymentAmount: number;
};

const TipPicker = ({ onClose, onSubmit, title, submitText, paymentAmount }: Props) => {
  const { i18n } = useI18n();
  const [highlightedOption, setHighlightedOption] = React.useState<number | null>(null);

  const currency = useCurrency();
  const isStorybook = !!process.env.STORYBOOK_MODE;

  const handleSubmit = (amount: number) => {
    onSubmit(amount);
  };

  const handleTouchEnd = (amount: number) => {
    if (amount === highlightedOption) {
      const tip = Math.round(paymentAmount * (amount / 100));
      handleSubmit(tip);
    }
    setHighlightedOption(null);
  };

  const AMOUNTS = [5, 10, 15, 20];

  return (
    <Wrapper data-test="numpad">
      <InnerWrapper>
        <Amount>{currency.format(paymentAmount)}</Amount>
        <Title>{title}</Title>

        <TipOptions>
          {AMOUNTS.map((amount) => (
            <TipOption
              highlighted={highlightedOption === amount}
              onTouchStart={() => {
                setHighlightedOption(amount);
              }}
              onTouchEnd={() => {
                handleTouchEnd(amount);
              }}>
              <TipAmount>{amount}%</TipAmount>
              <TipTotal>{currency.format((paymentAmount * amount) / 100)}</TipTotal>
            </TipOption>
          ))}
        </TipOptions>
        <Button
          text={submitText || i18n('Handy.Payment.NoTip')}
          onTouchStart={() => {
            handleSubmit(0);
          }}
          height="72px"
          styleType="Default"
        />
      </InnerWrapper>
    </Wrapper>
  );
};

export default TipPicker;
